const IMAGE_SIZE_BREAKPOINTS: Record<string, number> = {
  small: 150,
  medium: 300,
  large: 600,
  xlarge: 1200,
};

function unique(values: string[]) {
  return [...new Set(values.filter(Boolean))];
}

function parseCsvEnv(name: string) {
  const value = process.env[name];
  if (!value) {
    return [];
  }

  return value
    .split(',')
    .map(entry => entry.trim())
    .filter(Boolean);
}

function toOrigin(value: string) {
  if (!value) {
    return '';
  }

  try {
    const url = new URL(value.includes('://') ? value : `https://${value}`);
    return url.origin;
  } catch {
    return '';
  }
}

function toHostname(value: string) {
  if (!value) {
    return '';
  }

  try {
    const url = new URL(value.includes('://') ? value : `https://${value}`);
    return url.hostname;
  } catch {
    return value.replace(/^https?:\/\//, '').split('/')[0] ?? '';
  }
}

function stripSizedSegment(source: string, size: string) {
  const index = source.indexOf(`/${size}/`);
  if (index === -1) {
    return source;
  }

  return source.slice(index + size.length + 1);
}

function ensureLeadingSlash(source: string) {
  return source.startsWith('/') ? source : `/${source}`;
}

function resolveImageSize(width: number) {
  return (
    Object.keys(IMAGE_SIZE_BREAKPOINTS).find(
      key => width <= IMAGE_SIZE_BREAKPOINTS[key],
    ) || 'xlarge'
  );
}

export function getImageCdnRoot() {
  return toOrigin(process.env.NEXT_PUBLIC_IMAGE_CDN_ROOT ?? '');
}

export function getResizableImageDomains() {
  return parseCsvEnv('NEXT_PUBLIC_IMAGE_RESIZABLE_DOMAINS')
    .map(toOrigin)
    .filter(Boolean);
}

export function getRemoteImageHosts() {
  return unique([
    toHostname(getImageCdnRoot()),
    ...getResizableImageDomains().map(toHostname),
    ...parseCsvEnv('NEXT_PUBLIC_IMAGE_REMOTE_HOSTS')
      .map(toHostname)
      .filter(Boolean),
  ]);
}

export function buildImageUrl(src: string, width = 1920) {
  const size = resolveImageSize(width);

  if (src.endsWith('.svg')) {
    return src;
  }

  if (getResizableImageDomains().some(domain => src.includes(domain))) {
    return `${stripSizedSegment(src, size)}?width=${width}`;
  }

  if (
    src.startsWith('https://') ||
    src.startsWith('http://') ||
    src.startsWith('/_next')
  ) {
    return src;
  }

  const imageCdnRoot = getImageCdnRoot();
  if (!imageCdnRoot) {
    return src;
  }

  return `${imageCdnRoot}/${size}${ensureLeadingSlash(
    stripSizedSegment(src, size),
  )}?width=${width}`;
}
